import { JobsitePreOnboardingStep } from 'apollo/generated/client-operations';
import { DocTypeKey } from 'types';
import {
  SelfOnboardingStepKey,
  SelfOnboardingStepConfig,
  JobsiteModule,
  JobsiteFeaturesModule,
  JobsiteOnboardingModule,
  JobsitePreOnboardingModule,
  JobsiteOnboardingDocument,
  Jobsite,
  DocumentType,
  SelfOnboardingNavigationStepKey,
} from 'containers/selfOnboarding/types';
import { OnboardingStepKey } from 'containers/workerOnboarding/types';
import { isStringEnumValue } from 'utils';
import { DocumentKey } from 'containers/worker/utils';
import {
  getSequentialSteps,
  SequentialConfigStep,
  SequentialStepArgs,
  SequentialStep,
} from './useSelfOnboardingNavigation.config';

export const getFeaturesModule = (
  modules: JobsiteModule[],
): JobsiteFeaturesModule =>
  modules?.find((m): m is JobsiteFeaturesModule => {
    return m.__typename === 'JobsiteFeaturesModule'; // eslint-disable-line no-underscore-dangle
  });

export const getOnboardingModule = (
  modules: JobsiteModule[],
): JobsiteOnboardingModule =>
  modules?.find((m): m is JobsiteOnboardingModule => {
    return m.__typename === 'JobsiteOnboardingModule'; // eslint-disable-line no-underscore-dangle
  });

export const getPreOnboardingModule = (
  modules: JobsiteModule[],
): JobsitePreOnboardingModule =>
  modules?.find((m): m is JobsitePreOnboardingModule => {
    return m.__typename === 'JobsitePreOnboardingModule'; // eslint-disable-line no-underscore-dangle
  });

export const getStepsConfig = (
  modules: JobsiteModule[],
): Partial<Record<string, JobsitePreOnboardingStep>> => {
  const module = getPreOnboardingModule(modules);
  return (
    module?.steps?.reduce(
      (result, step) => ({ ...result, [step.key]: step }),
      {},
    ) ?? {}
  );
};

const onboardingStepKey: Partial<Record<SelfOnboardingStepKey, string>> = {
  [SelfOnboardingStepKey.BasicInfo]: OnboardingStepKey.PersonalInfo,
  [SelfOnboardingStepKey.Profile]: OnboardingStepKey.PersonalInfo,
  [SelfOnboardingStepKey.EmergencyContactInfo]: OnboardingStepKey.PersonalInfo,
};

// this config is generated based on onboarding module
export const getStepConfig = (
  modules: JobsiteModule[],
  stepKey: SelfOnboardingNavigationStepKey,
): SelfOnboardingStepConfig => {
  const module = getOnboardingModule(modules);

  // get the step config from document if the step corresponds to a document
  const stepConfig = isStringEnumValue(DocumentKey, stepKey)
    ? module?.documents?.find(
        (doc) => (doc.workerDocTypeKey ?? doc.key) === stepKey,
      )
    : module?.steps?.find(
        (step) =>
          step.key ===
          (onboardingStepKey[stepKey as SelfOnboardingStepKey] ?? stepKey),
      );

  if (stepConfig) {
    return {
      ...stepConfig,
      fields:
        stepConfig.fields?.reduce(
          (result, field) => ({ ...result, [field.key]: field }),
          {},
        ) ?? {},
    };
  }

  return { key: stepKey, name: '', fields: {} };
};

export const getDocument = (
  key: DocTypeKey,
  modules: JobsiteModule[],
): JobsiteOnboardingDocument => {
  const jobsiteOnboardingModule = getOnboardingModule(modules);
  return jobsiteOnboardingModule?.documents?.find((doc) => doc.key === key);
};

export const isDocumentVisible = (
  key: DocTypeKey,
  modules: JobsiteModule[],
): boolean => {
  if (!modules?.length) return false;
  const jobsiteOnboardingModule = getOnboardingModule(modules);
  return !!jobsiteOnboardingModule?.documents?.some(
    (doc) => doc.key === key && doc.isVisible,
  );
};

export const isDocumentRequired = (
  key: DocTypeKey,
  modules: JobsiteModule[],
): boolean => {
  if (!modules?.length) return false;
  const jobsiteOnboardingModule = getOnboardingModule(modules);
  return !!jobsiteOnboardingModule?.documents?.some(
    (doc) => doc.key === key && doc.isRequired,
  );
};

export const getVisibleDocuments = (modules: JobsiteModule[]): string[] => {
  const jobsiteOnboardingModule = getOnboardingModule(modules);
  return jobsiteOnboardingModule?.documents
    ?.filter((doc) => doc.isVisible)
    .map(({ key }) => key);
};

export const getMultiInstanceDocumentTypesByWorkerDocType = (
  jobsite: Jobsite,
): Record<string, DocumentType[]> => {
  if (!jobsite) return {};
  return (
    jobsite?.documentTypes.edges
      // only multi-instance document types have a key at jobsiteWorkerDocumentType level
      .filter(({ node }) => node.key)
      .reduce<Record<string, DocumentType[]>>((acc, { node: dt }) => {
        const { key: wdtKey } = dt.workerDocumentType;
        if (!acc[wdtKey]) acc[wdtKey] = [];
        acc[wdtKey].push(dt);
        return acc;
      }, {} as Record<string, DocumentType[]>) ?? {}
  );
};

/**
 * Expands + filters an explicit list of sequential steps (multi-instance
 * document fan-out, then per-step visibility). The forked authenticated flow
 * passes its own step list, so the shared util needn't know about the flag.
 */
export const getVisibleSequentialStepsFrom = (
  sequentialSteps: SequentialConfigStep[],
  args: Omit<SequentialStepArgs, 'getStepConfig'>,
): SequentialStep[] => {
  const { jobsite } = args;
  const { modules: jobsiteModules } = jobsite ?? {};
  const multiInstanceDocumentTypesByWorkerDocType =
    getMultiInstanceDocumentTypesByWorkerDocType(jobsite);

  const sequentialStepsWithMultiInstanceDocuments = sequentialSteps.flatMap(
    (step): SequentialStep | SequentialStep[] => {
      const stepKey = typeof step === 'string' ? step : step.key;
      const multiInstanceDocTypes =
        multiInstanceDocumentTypesByWorkerDocType[stepKey];
      if (multiInstanceDocTypes) {
        return multiInstanceDocTypes
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map(
            (dt): SequentialStep => ({
              key: dt.key,
              workerDocTypeKey: stepKey,
              isVisible: (): boolean => true,
            }),
          );
      }
      return step as SequentialStep;
    },
  );
  const visibleDocuments = getVisibleDocuments(jobsiteModules);
  return sequentialStepsWithMultiInstanceDocuments.filter((step) => {
    if (typeof step === 'string') {
      return (
        !isStringEnumValue(DocumentKey, step) ||
        visibleDocuments?.includes(step)
      );
    }
    const configStepKey = step.workerDocTypeKey ?? step.key;
    return (
      step.isVisible({
        ...args,
        getStepConfig: () => getStepConfig(jobsiteModules, configStepKey),
      }) &&
      (!isStringEnumValue(DocumentKey, configStepKey) ||
        visibleDocuments?.includes(step.key))
    );
  });
};

export const getVisibleSequentialSteps = (
  args: Omit<SequentialStepArgs, 'getStepConfig'>,
): SequentialStep[] =>
  getVisibleSequentialStepsFrom(
    getSequentialSteps(args.authenticatedSelfOnboardingEnabled),
    args,
  );

export function jobsiteDoesNotAllowToSkipSteps(
  modules: JobsiteModule[],
): boolean {
  return !!getFeaturesModule(modules)?.doNotAllowToSkipSelfOnboardingSteps;
}
